import {
  createContext,
  createEffect,
  createMemo,
  onCleanup,
  type ParentComponent,
  useContext,
} from "solid-js";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { createPowerSyncQuery } from "@listto/shared/solid";
import { useUserPreferences } from "@/providers/user-preferences-context";
import {
  getFontFamilyQuery,
  getFontFamilyVariantsQuery,
  getFontKey,
  mapFontFamilyWithVariants,
  queryKeys,
  resolveFontId,
  typographyMachine,
} from "@listto/shared";
import { createActor } from "xstate";
import { fromActorRef } from "@xstate/solid";
import { FontDownloaderService } from "@/utils/font-downloader";

interface TypographyContextValue {
  fontFamily: () => string | null;
  fontFamilyName: () => string | null;
  loadedRev: () => number;
  isTypoLoading: () => boolean;
}

const TypographyContext = createContext<TypographyContextValue>();

export const TypographyProvider: ParentComponent = (props) => {
  const { db: psDb } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { preferences, isLoading: isPreferencesLoading } = useUserPreferences();

  const fontFamilyId = createMemo(() => preferences()?.font_family ?? null);

  // 2. Query specific font family details
  const familyData = createPowerSyncQuery({
    queryKey: () => queryKeys.fonts.family(fontFamilyId()),
    db: psDb,
    queryFn: () => {
      const db = drizzle();
      const fid = fontFamilyId();
      if (!db || !fid) {
        return null;
      }
      return getFontFamilyQuery(db, fid);
    },
    enabled: () => Boolean(fontFamilyId()) && Boolean(drizzle()),
    // Non-suspending — always-mounted provider; see note below.
    suspense: false,
  });

  const resolvedId = createMemo(() => resolveFontId(familyData.data ?? [], fontFamilyId()));

  // 3. Query font variants
  const variantsData = createPowerSyncQuery({
    queryKey: () => queryKeys.fonts.familyVariants(fontFamilyId()),
    db: psDb,
    queryFn: () => {
      const db = drizzle();
      const rid = resolvedId();
      if (!db || !rid) {
        return null;
      }
      return getFontFamilyVariantsQuery(db, rid);
    },
    enabled: () => Boolean(resolvedId()) && Boolean(drizzle()),
    // Non-suspending — always-mounted provider. Suspending here would blank the
    // whole shell behind the root <Suspense> until the DB/font query resolves.
    suspense: false,
  });

  const fontData = createMemo(() => {
    if (!familyData.data || !variantsData.data) {
      return null;
    }
    return mapFontFamilyWithVariants(familyData.data, variantsData.data);
  });

  // Initialize XState Machine
  const actor = createActor(typographyMachine, {
    input: {
      downloader: FontDownloaderService,
      getFontKey,
    },
  });
  actor.start();
  onCleanup(() => {
    actor.stop();
  });

  const state = fromActorRef(actor);

  // Sync data to the machine when queries resolve
  createEffect(() => {
    const ps = psDb();
    if (!ps) {
      return;
    }

    actor.send({
      type: "SYNC_DATA",
      fontFamilyId: fontFamilyId(),
      fontData: fontData(),
      db: ps,
    });
  });

  // Apply CSS custom property globally to document.documentElement
  createEffect(() => {
    const activeName = state().context.activeFamilyName;
    const root = document.documentElement;
    if (activeName) {
      // Keep a generic fallback chain in the value itself so that if the
      // chosen font fails to load (offline, CDN hiccup) text stays readable
      // instead of falling back to nothing.
      root.style.setProperty(
        "--font-family",
        `'${activeName}', ui-sans-serif, system-ui, sans-serif`,
      );
    } else {
      root.style.removeProperty("--font-family");
    }
  });

  const isTypoLoading = () =>
    isPreferencesLoading() ||
    familyData.isLoading ||
    variantsData.isLoading ||
    state().matches("downloading") ||
    state().matches("evaluating");

  return (
    <TypographyContext.Provider
      value={{
        fontFamily: () => state().context.activeFamilyId,
        fontFamilyName: () => state().context.activeFamilyName,
        loadedRev: () => state().context.loadedRev,
        isTypoLoading,
      }}
    >
      {props.children}
    </TypographyContext.Provider>
  );
};

export const useTypography = () => {
  const ctx = useContext(TypographyContext);
  if (!ctx) {
    throw new Error("useTypography must be used within TypographyProvider");
  }
  return ctx;
};
